import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import Cookies from "js-cookie";
import {
  varselLydPa, settVarselLyd, spillVarselLyd, varselLydVolum, settVarselLydVolum,
} from "../utils/varselLyd";
import { useErLeder } from "../employeeAccess";

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/+$/, "");

const TYPE_COLORS = {
  workshop: "bg-purple-100 text-purple-800",
  offer: "bg-blue-100 text-blue-800",
  booking: "bg-green-100 text-green-800",
  chat: "bg-teal-100 text-teal-800",
  akutt: "bg-red-100 text-red-800",
  vippsPayment: "bg-orange-100 text-orange-800",
};

// Farge på "kom fra"-merket på bookinger som er godkjent/booket av mekaniker
// fra en akutt- eller chat-henvendelse (se createdBy.source), samme farger
// som de respektive seksjonene ellers på siden.
const ORIGIN_COLORS = {
  Akutt: "bg-red-50 text-red-700 border border-red-200",
  Chat: "bg-teal-50 text-teal-700 border border-teal-200",
};

const fmtDT = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  const now = new Date();
  const diffMs = now - dt;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "Nå nettopp";
  if (diffMin < 60) return `${diffMin} min siden`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH} t siden`;
  return dt.toLocaleDateString("no-NO", { day: "2-digit", month: "2-digit" });
};

export default function Varsling() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [error, setError] = useState("");

  const [chatMessages, setChatMessages] = useState([]);
  const [akuttRequests, setAkuttRequests] = useState([]);
  const [lydPa, setLydPa] = useState(varselLydPa());

  // Av som standard. Slås den på, spilles lyden én gang så man hører hvordan den er.
  const byttLyd = () => {
    const pa = !lydPa;
    settVarselLyd(pa);
    setLydPa(pa);
    if (pa) spillVarselLyd();
  };

  // Volumet lagres med en gang; lyden spilles når man har sluttet å dra.
  const [volum, setVolum] = useState(varselLydVolum());
  const proveLyd = useRef(null);
  const endreVolum = (e) => {
    const v = Number(e.target.value);
    setVolum(v);
    settVarselLydVolum(v);
    clearTimeout(proveLyd.current);
    proveLyd.current = setTimeout(spillVarselLyd, 300);
  };
  useEffect(() => () => clearTimeout(proveLyd.current), []);

  const butikkid = Cookies.get("butikkid");
  const erLeder = useErLeder();
  const [merkFeil, setMerkFeil] = useState("");

  // Leder merker et varsel om verksted/pristilbud som lest når den ansvarlige ikke kan
  // (src/lib/varselAnsvarlig.js). Backend sjekker at den valgte ansatte er leder.
  const merkLest = async (item) => {
    setMerkFeil("");
    try {
      const r = await fetch(`${API_BASE_URL}/notifications/${item.type}/${item.id}/merk-lest`, {
        method: "POST",
        headers: { "X-Ansatt": encodeURIComponent(Cookies.get("selectedEmployee") || "") },
      });
      if (!r.ok) {
        const b = await r.json().catch(() => ({}));
        throw new Error(b.message || "Kunne ikke merke varselet som lest.");
      }
      await fetchNotifications();
    } catch (e) {
      setMerkFeil(e.message || "Kunne ikke merke varselet som lest.");
    }
  };

  const fetchChat = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE_URL}/chat?t=${Date.now()}`, { cache: "no-store" });
      if (!r.ok) return;
      setChatMessages(await r.json());
    } catch { /* silent */ }
  }, []);

  const fetchAkutt = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE_URL}/akutt?t=${Date.now()}`, { cache: "no-store" });
      if (!r.ok) return;
      setAkuttRequests(await r.json());
    } catch { /* silent */ }
  }, []);

  const fetchNotifications = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE_URL}/notifications/unread?t=${Date.now()}`, {
        cache: "no-store",
      });
      if (!r.ok) throw new Error();
      const data = await r.json();
      const filtered = butikkid
        ? (data.items || []).filter(
            (item) => !item.butikkid || String(item.butikkid) === String(butikkid)
          )
        : data.items || [];
      setItems(filtered);
      setLastUpdated(new Date());
      setError("");
    } catch {
      setError("Kunne ikke hente varsler.");
    } finally {
      setLoading(false);
    }
  }, [butikkid]);

  useEffect(() => {
    fetchNotifications();
    fetchChat();
    fetchAkutt();
    const id = setInterval(() => { fetchNotifications(); fetchChat(); fetchAkutt(); }, 30_000);
    return () => clearInterval(id);
  }, [fetchNotifications, fetchChat, fetchAkutt]);

  // Akutt har egen dedikert seksjon over som dekker både ubehandlede og
  // behandlede — ekskluder den helt fra den generiske Uleste/Leste-listen.
  // Chat sin dedikerte seksjon viser derimot kun ubesvarte (se GET /chat) —
  // så kun de skal ekskluderes her (unreadCount > 0 ≈ ubesvart); en besvart
  // chat har ingen annen seksjon å vises i og må derfor slippes gjennom til
  // "Behandlet siste 24 timer" (24 timer fra den ble lest/behandlet, se notifications.js).
  const generalItems = items.filter(
    (i) => i.type !== "akutt" && !(i.type === "chat" && i.unreadCount > 0)
  );
  const unreadItems = generalItems.filter((i) => i.unreadCount > 0);
  const readItems = generalItems.filter((i) => i.unreadCount === 0);

  // Behandlet = besvart, godkjent (booket), avvist eller stengt. Vises i 24 timer (GET /akutt).
  const akuttBehandlet = (a) => Boolean(a.besvart || a.godkjent || a.avvist || a.stengt);
  const akuttStatus = (a) => (a.godkjent ? "Booket" : a.avvist ? "Avvist" : a.stengt ? "Stengt" : a.besvart ? "Besvart" : "");
  const ubehandledeAkutt = akuttRequests.filter((a) => !akuttBehandlet(a));
  const behandledeAkutt = akuttRequests.filter(akuttBehandlet);

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Varsling</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Nye kundebeskjeder · siste 24 timer
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={byttLyd}
            aria-pressed={lydPa}
            title="Gjelder bare denne maskinen"
            className={`px-3 py-1.5 rounded-lg text-sm border ${
              lydPa
                ? "bg-teal-50 border-teal-300 text-teal-800 hover:bg-teal-100"
                : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
            }`}
          >
            <span aria-hidden="true">{lydPa ? "🔔" : "🔕"}</span> Lyd ved nye varsler
            <span aria-hidden="true">: {lydPa ? "på" : "av"}</span>
          </button>
          {lydPa && (
            <label className="flex items-center gap-2 text-sm text-gray-700">
              Volum
              <input
                type="range"
                min="5"
                max="100"
                step="5"
                value={volum}
                onChange={endreVolum}
                aria-valuetext={`${volum} prosent`}
                className="w-24 accent-teal-700"
              />
            </label>
          )}
          {lastUpdated && (
            <span className="text-xs text-gray-400">
              Oppdatert {lastUpdated.toLocaleTimeString("no-NO", { hour: "2-digit", minute: "2-digit" })}
            </span>
          )}
          <button
            onClick={() => { setLoading(true); fetchNotifications(); fetchChat(); fetchAkutt(); }}
            className="px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-sm text-gray-700"
          >
            Oppdater
          </button>
        </div>
      </div>

      {merkFeil && (
        <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {merkFeil}
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Akutte forespørsler */}
      {akuttRequests.length > 0 && (
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-red-600 uppercase tracking-wide">
              🚨 Akutte forespørsler ({ubehandledeAkutt.length} ubehandlet)
            </h2>
            <Link to="/chat-historikk" className="text-xs text-red-700 hover:underline">
              Se historikk →
            </Link>
          </div>
          <div className="space-y-2">
            {[...ubehandledeAkutt, ...behandledeAkutt].map((akutt) => {
              const bikeInfo = [akutt.merke, akutt.modell].filter(Boolean).join(" ");
              const unreadCount = (akutt.messages || []).filter(
                (m) => m.from === "customer" && !m.readAt
              ).length;
              const lastMsg = [...(akutt.messages || [])].reverse()[0];

              return (
                <button
                  key={akutt._id}
                  type="button"
                  onClick={() => navigate(`/akutt-detalj/${akutt._id}`)}
                  className={`w-full text-left rounded-xl border shadow-sm px-4 py-3 flex items-start justify-between gap-3 transition hover:shadow-md active:scale-[0.99] ${
                    !akuttBehandlet(akutt)
                      ? "border-red-300 bg-white ring-1 ring-red-200"
                      : "border-gray-200 bg-white opacity-75"
                  }`}
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-semibold rounded-full px-2 py-0.5 bg-red-100 text-red-800">
                      Akutt
                    </span>
                    <span className="font-semibold text-gray-900 text-sm">{akutt.navn}</span>
                    <a
                      href={`tel:${akutt.telefon}`}
                      className="text-xs text-blue-600 hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {akutt.telefon}
                    </a>
                    {bikeInfo && (
                      <span className="text-xs text-gray-500 bg-gray-100 rounded px-1.5 py-0.5">
                        {bikeInfo}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {unreadCount > 0 && (
                      <span className="bg-red-500 text-white text-xs font-bold rounded-full min-w-[22px] h-[22px] flex items-center justify-center px-1.5">
                        {unreadCount}
                      </span>
                    )}
                    {akuttBehandlet(akutt) && unreadCount === 0 && (
                      <span className="text-xs text-green-700 font-medium">{akuttStatus(akutt)}</span>
                    )}
                    {lastMsg && (
                      <span className="text-xs text-gray-400">{fmtDT(lastMsg.createdAt)}</span>
                    )}
                    <span className="text-gray-400 text-sm">→</span>
                  </div>
                </button>
              );
            })}
          </div>
        </section>
      )}

      {/* Chat-henvendelser */}
      {chatMessages.length > 0 && (
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
              Chat-henvendelser ({chatMessages.length} ubesvart)
            </h2>
            <Link to="/chat-historikk" className="text-xs text-teal-700 hover:underline">
              Se samtalehistorikk →
            </Link>
          </div>
          <div className="space-y-2">
            {chatMessages.map((chat) => {
              const bikeInfo = [chat.merke, chat.modell].filter(Boolean).join(" ");
              const unreadCount = (chat.messages || []).filter(
                (m) => m.from === "customer" && !m.readAt
              ).length;
              const lastMsg = [...(chat.messages || [])].reverse()[0];

              return (
                <button
                  key={chat._id}
                  type="button"
                  onClick={() => navigate(`/chat-detalj/${chat._id}`)}
                  className="w-full text-left rounded-xl border shadow-sm px-4 py-3 flex items-start justify-between gap-3 transition hover:shadow-md active:scale-[0.99] border-teal-300 bg-white ring-1 ring-teal-200"
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-semibold rounded-full px-2 py-0.5 bg-teal-100 text-teal-800">
                      Chat
                    </span>
                    <span className="font-semibold text-gray-900 text-sm">{chat.navn}</span>
                    <a
                      href={`tel:${chat.telefon}`}
                      className="text-xs text-blue-600 hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {chat.telefon}
                    </a>
                    {bikeInfo && (
                      <span className="text-xs text-gray-500 bg-gray-100 rounded px-1.5 py-0.5">
                        {bikeInfo}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {unreadCount > 0 && (
                      <span className="bg-teal-500 text-white text-xs font-bold rounded-full min-w-[22px] h-[22px] flex items-center justify-center px-1.5">
                        {unreadCount}
                      </span>
                    )}
                    {lastMsg && (
                      <span className="text-xs text-gray-400">{fmtDT(lastMsg.createdAt)}</span>
                    )}
                    <span className="text-gray-400 text-sm">→</span>
                  </div>
                </button>
              );
            })}
          </div>
        </section>
      )}

      {loading ? (
        <div className="text-center py-12 text-gray-400">Laster varsler…</div>
      ) : generalItems.length === 0 && chatMessages.length === 0 && akuttRequests.length === 0 ? (
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm p-10 text-center">
          <div className="text-4xl mb-3">✅</div>
          <p className="text-gray-600 font-medium">Ingen nye meldinger</p>
          <p className="text-sm text-gray-400 mt-1">Ingen uleste kundebeskjeder de siste 24 timene</p>
        </div>
      ) : (
        <div className="space-y-6">
          {unreadItems.length > 0 && (
            <section>
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
                Ikke behandlet ({unreadItems.length})
              </h2>
              <div className="space-y-3">
                {unreadItems.map((item) => (
                  <NotificationCard
                    key={`${item.type}-${item.id}`}
                    item={item}
                    onClick={() => navigate(item.detailPath)}
                    highlight
                    onMerkLest={erLeder && item.ansvarlig ? () => merkLest(item) : null}
                  />
                ))}
              </div>
            </section>
          )}

          {readItems.length > 0 && (
            <section>
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
                Behandlet siste 24 timer ({readItems.length})
              </h2>
              <div className="space-y-3">
                {readItems.map((item) => (
                  <NotificationCard
                    key={`${item.type}-${item.id}`}
                    item={item}
                    onClick={() => navigate(item.detailPath)}
                    highlight={false}
                  />
                ))}
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
}

// Uleste varsler om verksted og pristilbud venter på den ansvarlige (siste melding eller tilbudet).
// `onMerkLest` (bare leder) gir en egen knapp under kortet, utenfor kortets egen knapp.
function NotificationCard({ item, onClick, highlight, onMerkLest }) {
  const venter = item.unreadCount > 0 && item.ansvarlig;
  return (
    <div className={venter ? "space-y-1" : undefined}>
      <button
        onClick={onClick}
        className={`w-full text-left rounded-xl border shadow-sm p-4 transition hover:shadow-md active:scale-[0.99] ${
          highlight
            ? "border-blue-300 bg-white ring-1 ring-blue-200"
            : "border-gray-200 bg-white opacity-75"
        }`}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-xs font-semibold rounded-full px-2 py-0.5 ${TYPE_COLORS[item.type] || "bg-gray-100 text-gray-700"}`}>
              {item.typeLabel}
            </span>
            {item.origin && (
              <span className={`text-xs font-medium rounded-full px-2 py-0.5 ${ORIGIN_COLORS[item.origin] || "bg-gray-100 text-gray-600 border border-gray-200"}`}>
                fra {item.origin}
              </span>
            )}
            <span className="font-semibold text-gray-900 text-sm">{item.customerName}</span>
            <span className="text-xs text-gray-400">#{item.ref}</span>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {item.unreadCount > 0 && (
              <span className="bg-red-500 text-white text-xs font-bold rounded-full min-w-[22px] h-[22px] flex items-center justify-center px-1.5">
                {item.unreadCount}
              </span>
            )}
            {/* Behandlede: når de ble lest/behandlet (forsvinner 24 timer etter det). */}
            <span className="text-xs text-gray-500">
              {!item.unreadCount && item.behandletAt ? `Behandlet ${fmtDT(item.behandletAt)}` : fmtDT(item.lastMessage?.createdAt)}
            </span>
            <span className="text-gray-400 text-sm">→</span>
          </div>
        </div>
        {item.lastMessage && (
          <p className="mt-2 text-sm text-gray-600 line-clamp-2 text-left">
            {item.lastMessage.text}
          </p>
        )}
        {venter && (
          <p className="mt-2 text-xs text-gray-700 text-left">
            Ulest til <span className="font-semibold">{item.ansvarlig}</span> har åpnet saken
          </p>
        )}
      </button>
      {venter && onMerkLest && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={onMerkLest}
            className="px-3 py-1 rounded-lg border border-gray-300 bg-white text-xs text-gray-800 hover:bg-gray-50"
          >
            Merk som lest<span className="sr-only"> for {item.customerName}, #{item.ref}</span>
          </button>
        </div>
      )}
    </div>
  );
}
